import { useState } from 'react';
import { ArrowLeft, Heart, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { healthDataService, BloodPressureEntryDto } from '../services/health-data.service';
import { LoadingSkeleton } from './LoadingSkeleton';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';

interface BloodPressureTrackerProps {
  onBack: () => void;
}

export function BloodPressureTracker({ onBack }: BloodPressureTrackerProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [systolic, setSystolic] = useState('');
  const [diastolic, setDiastolic] = useState('');
  const [notes, setNotes] = useState('');
  const [showForm, setShowForm] = useState(false);

  // Fetch blood pressure entries
  const {
    data: entriesData,
    isLoading: entriesLoading,
  } = useQuery({
    queryKey: ['blood-pressure-entries', user?.id],
    queryFn: () => healthDataService.getBloodPressureEntries({ limit: 50 }),
    enabled: !!user,
  });

  const entries = entriesData?.data || entriesData || [];

  // Create blood pressure entry mutation
  const createMutation = useMutation({
    mutationFn: (data: BloodPressureEntryDto) => healthDataService.createBloodPressureEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blood-pressure-entries'] });
      toast.success('Blood pressure logged successfully!');
      setSystolic('');
      setDiastolic('');
      setNotes('');
      setShowForm(false);
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!systolic || !diastolic || isNaN(Number(systolic)) || isNaN(Number(diastolic))) {
      toast.error('Please enter valid systolic and diastolic values');
      return;
    }

    createMutation.mutate({
      systolic: Number(systolic),
      diastolic: Number(diastolic),
      date: new Date().toISOString(),
      notes: notes.trim() || undefined,
    });
  };

  const getBPStatus = (systolic: number, diastolic: number) => {
    if (systolic < 90 || diastolic < 60) {
      return { label: 'Low', color: 'text-dropsugar-green', bg: 'bg-dropsugar-green/10' };
    }
    if (systolic >= 140 || diastolic >= 90) {
      return { label: 'High', color: 'text-red-600', bg: 'bg-red-50' };
    }
    if (systolic >= 120 || diastolic >= 80) {
      return { label: 'Elevated', color: 'text-orange-600', bg: 'bg-orange-50' };
    }
    return { label: 'Normal', color: 'text-green-600', bg: 'bg-green-50' };
  };

  if (entriesLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-700 hover:text-dropsugar-green transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Blood Pressure Tracking</h1>
            <div className="w-20" />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            className="w-full bg-gradient-to-r from-dropsugar-blue to-dropsugar-blue-dark text-white py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all mb-8"
          >
            + Add Blood Pressure Reading
          </button>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-bold mb-4">New Blood Pressure Reading</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Systolic (mmHg) *
                  </label>
                  <input
                    type="number"
                    value={systolic}
                    onChange={(e) => setSystolic(e.target.value)}
                    placeholder="e.g., 120"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                    required
                    min="0"
                    max="300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Diastolic (mmHg) *
                  </label>
                  <input
                    type="number"
                    value={diastolic}
                    onChange={(e) => setDiastolic(e.target.value)}
                    placeholder="e.g., 80"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                    required
                    min="0"
                    max="200"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="flex-1 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Reading'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setSystolic('');
                    setDiastolic('');
                    setNotes('');
                  }}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <Heart className="w-6 h-6 mr-2 text-dropsugar-green" />
            Recent Readings
          </h2>
          {entries.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No readings yet. Add your first reading above!</p>
          ) : (
            <div className="space-y-3">
              {entries.map((entry: any) => {
                const status = getBPStatus(entry.systolic, entry.diastolic);
                const date = new Date(entry.date || entry.createdAt);
                return (
                  <div
                    key={entry.id}
                    className={`p-4 rounded-lg border ${status.bg} border-gray-200`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center space-x-3">
                          <span className={`text-2xl font-bold ${status.color}`}>
                            {entry.systolic}/{entry.diastolic} mmHg
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${status.color} ${status.bg}`}>
                            {status.label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {date.toLocaleDateString()} {date.toLocaleTimeString()}
                        </p>
                        {entry.notes && (
                          <p className="text-sm text-gray-500 mt-1">{entry.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
